import React from "react";
import {
  Box,
  Chip,
  MenuItem,
  Typography,
  Divider,
  Skeleton,
  Button,
  Tooltip,
} from "@mui/material";
import { Clock, MoreHorizontal, User, Refresh02 } from "@tiny/icons";
import { useDrawerInvokers } from "@tiny/lib";
import { Handle, NodeProps, Position } from "reactflow";
import { Node, NodeData, TimeDelayNodeStats } from "../types";
import { MenuIconButton } from "../../../../components";
import useDeleteNode from "../Hooks/useDeleteNode";
import TimeDelayForm from "../../../../customComponents/TimeDelayForm";
import DelayDateNote from "../../../../customComponents/TimeDelayForm/DelayDateNote";
import { useWorkflowsBuilderPageView } from "../../WorkflowsBuilderPage.context";
import { formatNumber } from "@tiny/utils/formatUtils";
import {
  DATE_PASSED_WARNING,
  formatDelayCardLabel,
  isSpecificDatePassed,
} from "./Timing.helpers";

const TimingNode = ({ id, data }: NodeProps<NodeData>) => {
  const { deleteNode } = useDeleteNode();
  const { showDrawer } = useDrawerInvokers();
  const { configured } = data.config;
  const {
    viewMode,
    isStatsLoading,
    getWorkflowStatsError,
    refetchWorkflowStats,
    workflowStats,
    showNodeStats,
  } = useWorkflowsBuilderPageView();

  const nodeStats = workflowStats?.[id] as TimeDelayNodeStats | undefined;

  const handleRefreshStats = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    refetchWorkflowStats();
  };

  const renderStatsContent = () => {
    if (getWorkflowStatsError) {
      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="label2" fontSize={12} color="error">
            Failed to load stats
          </Typography>
          <Button
            size="small"
            startIcon={<Refresh02 sx={{ fontSize: 12 }} />}
            onClick={handleRefreshStats}
            sx={{ minWidth: "auto", fontSize: 12 }}
          >
            Reload
          </Button>
        </Box>
      );
    }

    if (isStatsLoading) {
      return (
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Box>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={30} height={14} />
            </Box>
          </Box>
        </Box>
      );
    }

    const queue = formatNumber(nodeStats?.queue || 0);

    return (
      <Tooltip
        title={`${queue} users are currently waiting in the delay queue`}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: 4,
              },
            },
          },
        }}
        arrow
      >
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <User sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="caption" color="grey.100" component="div">
              <strong>In Queue</strong>
              <Typography color="grey.80" component="span" ml={1}>
                {queue}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ backgroundColor: "transparent", border: "none" }}
      />
      <Box
        sx={(theme) => ({
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[80]}`,
          padding: 3,
          paddingBottom: 4,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          width: "400px",
          cursor: "pointer",
        })}
        onClick={(e) => {
          e.stopPropagation();
          showDrawer({
            children: <TimeDelayForm id={id} />,
          });
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mr: -1 }}
        >
          <Box display="flex" alignItems="center">
            <Clock
              sx={(theme) => ({
                fontSize: 20,
                mr: 1,
                color: theme.palette.yellow[150],
              })}
            />
            <Typography
              variant="label1"
              color="grey.80"
              sx={(theme) => ({
                textTransform: "uppercase",
                color: theme.palette.yellow[150],
              })}
            >
              TIMING
            </Typography>
          </Box>
          <MenuIconButton
            icon={<MoreHorizontal />}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              onClick={(e) => {
                showDrawer({
                  children: <TimeDelayForm id={id} />,
                });
              }}
            >
              {viewMode ? "Preview" : "Edit"}
            </MenuItem>
            {!viewMode && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNode(id);
                }}
              >
                Delete
              </MenuItem>
            )}
          </MenuIconButton>
        </Box>
        <Box>
          <Typography variant="p1" color="grey.100" fontWeight="600">
            {formatDelayCardLabel(data)}
          </Typography>
          {isSpecificDatePassed(data) && (
            <Box mt={1}>
              <DelayDateNote severity="warning">
                {DATE_PASSED_WARNING}
              </DelayDateNote>
            </Box>
          )}
        </Box>
        <Box mt={2}>
          {configured ? (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.primary[100],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                lineHeight: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Configured"
            />
          ) : (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.yellow[150],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                lineHeight: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Not-Configured"
            />
          )}
        </Box>
        {configured && showNodeStats && (
          <>
            <Divider sx={{ mt: 4 }} />
            <Box mt={2}>{renderStatsContent()}</Box>
          </>
        )}
      </Box>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ backgroundColor: "transparent", border: "none" }}
      />
    </>
  );
};

export default TimingNode;
